import React from "react";
import { caseConstants } from "@_constants/case.constants";

import NoteActivityCard from "./activityTypes/NoteActivityCard";
import EmailActivityCard from "./activityTypes/EmailActivityCard/EmailActivityCard";
import InvoiceActivityCard from "./activityTypes/InvoiceActivityCard";
import LetterActivityCard from "./activityTypes/LetterActivityCard";
import LicenseActivityCard from "./activityTypes/LicenseActivityCard";
import PaymentActivityCard from "./activityTypes/PaymentActivityCard";
import WebsiteOwnerActivityCard from "./activityTypes/WebsiteOwnerActivityCard";
import {
  CaseCreatedActivityCard,
  StateChangeActivityCard,
} from "./activityTypes/StateChangeActivityCard";

/**
 * CaseActivityCard
 *
 * Pure routing component. Inspects `activity.type` and delegates rendering
 * to the appropriate typed sub-card. Each sub-card owns its own state,
 * effects, and action handlers — this file contains no logic of its own.
 *
 * Props:
 *   activity        – the activity object from the backend
 *   caseData        – full case object (needed by email / license / letter cards)
 *   resumeAutomation – passed through to MailPopup
 *   emailTemplates  – list of email templates for the reply-with-template flow
 */
function CaseActivityCard({
  activity,
  caseData,
  resumeAutomation,
  emailTemplates,
}) {
  const { type } = activity;

  // ── Note ────────────────────────────────────────────────────────────────────
  if (type === caseConstants.CASEACTIVITY_TYPE_NOTE) {
    return <NoteActivityCard activity={activity} />;
  }

  // ── Email (in / out / scheduled) ────────────────────────────────────────────
  if (
    type === caseConstants.CASEACTIVITY_TYPE_EMAIL_IN ||
    type === caseConstants.CASEACTIVITY_TYPE_EMAIL_OUT ||
    type === caseConstants.CASEACTIVITY_TYPE_EMAIL_SCHEDULED
  ) {
    return (
      <EmailActivityCard
        activity={activity}
        caseData={caseData}
        resumeAutomation={resumeAutomation}
        emailTemplates={emailTemplates}
      />
    );
  }

  // ── Invoice ─────────────────────────────────────────────────────────────────
  if (type === caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED) {
    return (
      <InvoiceActivityCard
        activity={activity}
        caseData={caseData}
        resumeAutomation={resumeAutomation}
      />
    );
  }

  // ── Letter ──────────────────────────────────────────────────────────────────
  if (type === caseConstants.CASEACTIVITY_TYPE_LETTER_SENT) {
    return (
      <LetterActivityCard
        activity={activity}
        caseData={caseData}
        resumeAutomation={resumeAutomation}
      />
    );
  }

  // ── License ─────────────────────────────────────────────────────────────────
  if (
    type === caseConstants.CASEACTIVITY_TYPE_LICENSE_UPLOADED ||
    type === caseConstants.CASEACTIVITY_TYPE_LICENSE_ACCEPTED ||
    type === caseConstants.CASEACTIVITY_TYPE_LICENSE_REJECTED
  ) {
    return (
      <LicenseActivityCard
        activity={activity}
        caseData={caseData}
        resumeAutomation={resumeAutomation}
      />
    );
  }

  // ── Payment ─────────────────────────────────────────────────────────────────
  if (
    type === caseConstants.CASEACTIVITY_TYPE_PAYMENT_SUCCEEDED ||
    type === caseConstants.CASEACTIVITY_TYPE_PAYMENT_FAILED ||
    type === caseConstants.CASEACTIVITY_TYPE_PAYMENT_INITIATED ||
    type === caseConstants.CASEACTIVITY_TYPE_PAYMENT_RECEIVED
  ) {
    return <PaymentActivityCard activity={activity} />;
  }

  // ── Website owner portal events ─────────────────────────────────────────────
  if (
    type === caseConstants.CASEACTIVITY_TYPE_LINK_CLICKED ||
    type === caseConstants.CASEACTIVITY_TYPE_LOGIN ||
    type === caseConstants.CASEACTIVITY_TYPE_BILLING_ADDR_SET
  ) {
    return <WebsiteOwnerActivityCard activity={activity} />;
  }

  // ── State changes ───────────────────────────────────────────────────────────
  if (type === caseConstants.CASEACTIVITY_TYPE_STATE_CHANGE) {
    return <StateChangeActivityCard activity={activity} />;
  }

  if (type === caseConstants.CASEACTIVITY_TYPE_CASE_CREATED) {
    return <CaseCreatedActivityCard activity={activity} />;
  }

  // ── Unknown / unhandled type ────────────────────────────────────────────────
  // return null;?
}

export default CaseActivityCard;
